import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts'
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent'
import { AxisDomain } from 'recharts/types/util/types'

import { Spinner } from '@/components/ui'
import { BucketType } from '@/schema'
import { formatDisplayDate } from '@/utils'
import { DATE_FORMAT } from '@/utils/constants'

import {
  cardVariants,
  ChartDataKey,
  MONTH_FORMAT,
  PeriodMetricKey,
} from '../../constants'
import { usePeriodMetric } from '../../hooks/usePeriodMetric'

import { ChartTooltip } from './ChartTooltip'

const CHART_X_AXIS_KEY = 'bucket_date'
const DEFAULT_CHART_Y_AXIS_DOMAIN: AxisDomain = [0, 'auto']
const DEFAULT_STROKE_LINE_COLOR = 'var(--Colors-Blue-600)'
const DEFAULT_TICK_FORMAT = {
  fontSize: 12,
  fill: '#535862', // text-gray-light-600
}

const getYAxisLabelProps = (value: string) => {
  return {
    value,
    angle: -90,
    position: { x: 10, y: 0 },
    className: 'text-xs',
    fill: '#000', // text-black
  }
}

const getActiveDotProps = () => {
  return {
    r: 6,
    strokeWidth: 2,
    stroke: DEFAULT_STROKE_LINE_COLOR,
    fill: '#fff', // text-white
  }
}
type TPeriodMetricCardProps = {
  periodKey: PeriodMetricKey
}

export const PeriodMetricCard: React.FC<TPeriodMetricCardProps> = (props) => {
  const { periodKey } = props

  const [activeIndex, setActiveIndex] = React.useState<number | null>(null)

  const { aggregationPeriod, trendData, isLoading } = usePeriodMetric({
    key: periodKey,
  })

  const { t } = useTranslation()

  const title = React.useMemo(() => {
    switch (periodKey) {
      case PeriodMetricKey.REVIEW_TIME_TREND:
        return t('dashboard.periodMetrics.reviewTimeTrend')
      case PeriodMetricKey.RESOLUTION_RATE_TREND:
        return t('dashboard.periodMetrics.resolutionRateTrend')
      case PeriodMetricKey.AVG_DAYS_PER_FILE_TREND:
        return t('dashboard.periodMetrics.avgDaysPerFileTrend')

      default:
        return ''
    }
  }, [periodKey, t])

  const chartYAxisLabel = React.useMemo(() => {
    switch (periodKey) {
      case PeriodMetricKey.REVIEW_TIME_TREND:
        return 'Average Review Time (days)'
      case PeriodMetricKey.RESOLUTION_RATE_TREND:
        return 'Average Resolution Rate (%)'
      case PeriodMetricKey.AVG_DAYS_PER_FILE_TREND:
        return 'Average Days per TFL File (day)'
    }
  }, [periodKey])

  const chartTooltipLabel = React.useMemo(() => {
    switch (periodKey) {
      case PeriodMetricKey.REVIEW_TIME_TREND:
        return 'Average Review Time'
      case PeriodMetricKey.RESOLUTION_RATE_TREND:
        return 'Average Resolution Rate'
      case PeriodMetricKey.AVG_DAYS_PER_FILE_TREND:
        return 'Average Days per File'
    }
  }, [periodKey])

  const chartDataKey: ChartDataKey | '' = React.useMemo(() => {
    switch (periodKey) {
      case PeriodMetricKey.REVIEW_TIME_TREND:
        return ChartDataKey.REVIEW_TIME_TREND
      case PeriodMetricKey.RESOLUTION_RATE_TREND:
        return ChartDataKey.RESOLUTION_RATE_TREND
      case PeriodMetricKey.AVG_DAYS_PER_FILE_TREND:
        return ChartDataKey.AVG_DAYS_PER_FILE_TREND

      default:
        return ''
    }
  }, [periodKey])

  // Check if there's no data to display
  const hasNoData = React.useMemo(() => {
    if (!trendData) return true
    if (!trendData.data) return true
    if (Array.isArray(trendData.data) && trendData.data.length === 0)
      return true
    return false
  }, [trendData])

  // Custom tooltip wrapper that tracks active X value
  const CustomTooltip = React.useCallback(
    (props: TooltipContentProps<ValueType, NameType>) => {
      if (props?.active && props?.label) {
        const index =
          trendData?.data?.findIndex(
            (item) => item[CHART_X_AXIS_KEY] === props.label
          ) ?? null
        setActiveIndex(index)
      } else if (!props?.active) {
        setActiveIndex(null)
      }

      return <ChartTooltip {...props} chartLabel={chartTooltipLabel} />
    },
    [chartTooltipLabel, trendData?.data]
  )

  // Get the active data point's Y value for the reference line segment
  const activeDataPoint = React.useMemo(() => {
    if (activeIndex === null || !trendData?.data?.[activeIndex]) return null
    return trendData.data[activeIndex]
  }, [activeIndex, trendData])

  const referenceLineSegment = React.useMemo(() => {
    if (!activeDataPoint || !chartDataKey) return null
    const xValue = activeDataPoint[CHART_X_AXIS_KEY]

    // Type assertion needed because chartDataKey is a union type
    const dataPoint = activeDataPoint as unknown as Record<string, unknown>
    const yValue = dataPoint[chartDataKey]

    if (xValue == null || yValue == null || typeof yValue !== 'number')
      return null

    return [
      { x: xValue, y: 0 },
      { x: xValue, y: yValue },
    ] as const
  }, [activeDataPoint, chartDataKey])

  const bucketLabel = React.useMemo(() => {
    return aggregationPeriod === BucketType.week
      ? t('dashboard.columns.weekBucket')
      : t('dashboard.columns.monthBucket')
  }, [aggregationPeriod, t])

  return (
    <motion.div
      variants={cardVariants}
      className="flex-1 space-y-5 rounded-xl border border-solid border-secondary bg-primary p-6 shadow-sm"
    >
      <h3 className="m-0 text-lg font-semibold text-primary-900">{title}</h3>
      <div className="relative w-full h-[225px]">
        {(() => {
          if (isLoading) {
            return (
              <div className="absolute inset-0 flex justify-center items-center">
                <Spinner className="size-8 text-foreground-fg-brand-primary-600" />
              </div>
            )
          }

          if (hasNoData) {
            return (
              <div className="absolute top-0 left-0 right-0 bottom-9 flex flex-col justify-center items-center gap-3 text-center">
                <div className="flex flex-col gap-1">
                  <h3 className="p-0 m-0 font-semibold text-primary-900 text-md">
                    {t('dashboard.periodMetrics.noData.title')}
                  </h3>
                </div>
              </div>
            )
          }
          return null
        })()}
        <ResponsiveContainer width="100%" height="100%">
          <LineChart responsive data={trendData?.data}>
            <CartesianGrid strokeDasharray="4 3" stroke="#F5F5F5" />
            <XAxis
              dataKey={CHART_X_AXIS_KEY}
              axisLine={false}
              tickLine={false}
              tick={DEFAULT_TICK_FORMAT}
              tickFormatter={(value) =>
                formatDisplayDate(
                  value,
                  aggregationPeriod === BucketType.week
                    ? DATE_FORMAT
                    : MONTH_FORMAT
                )
              }
            />
            <YAxis
              dataKey={chartDataKey}
              axisLine={false}
              tickLine={false}
              tick={DEFAULT_TICK_FORMAT}
              width="auto"
              domain={DEFAULT_CHART_Y_AXIS_DOMAIN}
              label={getYAxisLabelProps(chartYAxisLabel)}
            />
            <Tooltip content={CustomTooltip} cursor={false} />
            {referenceLineSegment && (
              <ReferenceLine
                segment={referenceLineSegment}
                stroke={DEFAULT_STROKE_LINE_COLOR}
                strokeWidth={2}
                strokeDasharray="0 0"
              />
            )}
            <Line
              type="monotone"
              dataKey={chartDataKey}
              stroke={DEFAULT_STROKE_LINE_COLOR}
              strokeWidth={2}
              dot={false}
              activeDot={getActiveDotProps()}
              name={title}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="text-center">
        <span className="text-sm text-tertiary-600 font-medium">
          {bucketLabel}
        </span>
      </div>
    </motion.div>
  )
}
